import { createRootRoute, createRoute, createRouter, redirect, type RouterHistory } from '@tanstack/react-router';

import { RequireAuth } from '@/components/auth/RequireAuth';
import { AuthedLayout } from '@/components/layout/AuthedLayout';
import { RootLayout } from '@/components/layout/RootLayout';
import { AdminDetailPage } from '@/routes/admin-detail';
import { AdminNewPage } from '@/routes/admin-new';
import { AdminsPage, validateAdminsSearch } from '@/routes/admins';
import { ChangePasswordPage } from '@/routes/change-password';
import { HealthCheckPage } from '@/routes/health-check';
import { HomePage } from '@/routes/home';
import { LoginPage } from '@/routes/login';
import { MfaVerifyPage } from '@/routes/mfa-verify';
import { NotFoundPage } from '@/routes/not-found';
import { SettingsPage } from '@/routes/settings';
import { TenantDetailPage } from '@/routes/tenant-detail';
import { TenantNewPage } from '@/routes/tenant-new';
import { TenantsPage, validateTenantsSearch } from '@/routes/tenants';
import { useAuthStore } from '@/stores/auth';
import { useChallenge } from '@/stores/challenge';

// Arbre déclaré à la main (code-based) : pas de plugin ni de routeTree.gen.ts pour une poignée de pages
const rootRoute = createRootRoute({ component: RootLayout, notFoundComponent: NotFoundPage });

// Routes protégées : toutes sous ce layout sans chemin (garde + navigation latérale)
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  component: () => (
    <RequireAuth>
      <AuthedLayout />
    </RequireAuth>
  ),
});

const indexRoute = createRoute({ getParentRoute: () => authedRoute, path: '/', component: HomePage });
const tenantsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/tenants',
  validateSearch: validateTenantsSearch,
  component: TenantsPage,
});
// /tenants/new déclarée à côté de /tenants/$id : le routeur classe le segment statique avant le paramètre
const tenantNewRoute = createRoute({ getParentRoute: () => authedRoute, path: '/tenants/new', component: TenantNewPage });
const tenantDetailRoute = createRoute({ getParentRoute: () => authedRoute, path: '/tenants/$id', component: TenantDetailPage });
const adminsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admins',
  validateSearch: validateAdminsSearch,
  component: AdminsPage,
});
const adminNewRoute = createRoute({ getParentRoute: () => authedRoute, path: '/admins/new', component: AdminNewPage });
const adminDetailRoute = createRoute({ getParentRoute: () => authedRoute, path: '/admins/$id', component: AdminDetailPage });
const settingsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/settings', component: SettingsPage });
const changePasswordRoute = createRoute({ getParentRoute: () => authedRoute, path: '/settings/change-password', component: ChangePasswordPage });
export interface LoginSearch {
  reason?: 'expired';
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): LoginSearch =>
    search['reason'] === 'expired' ? { reason: 'expired' } : {},
  // Déjà connecté : l'accueil revérifie le jeton (/whoami) et renvoie ici s'il a expiré
  beforeLoad: () => {
    if (useAuthStore.getState().accessToken) throw redirect({ to: '/' });
  },
  component: LoginPage,
});
// Sans challenge en mémoire (accès direct, F5), l'écran MFA n'a rien à vérifier
const mfaVerifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mfa-verify',
  beforeLoad: () => {
    if (!useChallenge.getState().challengeToken) throw redirect({ to: '/login', search: { reason: 'expired' } });
  },
  component: MfaVerifyPage,
});
const healthCheckRoute = createRoute({ getParentRoute: () => rootRoute, path: '/health-check', component: HealthCheckPage });

export const routeTree = rootRoute.addChildren([
  authedRoute.addChildren([indexRoute, tenantsRoute, tenantNewRoute, tenantDetailRoute, adminsRoute, adminNewRoute, adminDetailRoute, settingsRoute, changePasswordRoute]),
  loginRoute,
  mfaVerifyRoute,
  healthCheckRoute,
]);

export const createAppRouter = (history?: RouterHistory) => createRouter({ routeTree, history });

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
